/**
 * AllocSampleReporter (QA Round B, 2026-06-04) — a lean 1Hz client heap
 * sampler that closes the "is the heap slowly climbing?" gap.
 *
 * BrowserPerfReporter already rolls heap + longtask + rAF into a 5s verdict
 * aimed at attributing a *frame spike*. This reporter is complementary and
 * deliberately minimal: it pushes the raw heap *level* once per second so the
 * server (AllocSampleBuffer → /api/diag/alloc-samples) can fit a slope and
 * surface a slow leak — a climb across minutes that produces no single GC
 * pause and so never trips the perf verdict.
 *
 * Memory source, in preference order:
 *   1. `measureUserAgentSpecificMemory()` — precise, async; only in
 *      cross-origin-isolated contexts (Chromium). Used when present.
 *   2. `performance.memory.usedJSHeapSize` — synchronous, Chromium-wide.
 *   3. neither (Firefox/Safari) → source='unsupported', heapUsedBytes=NaN.
 *      The sample still flows so the server knows a client is reporting; the
 *      aggregate flags `anyHeapSupported:false`.
 *
 * Gate + send conventions mirror BrowserPerfReporter exactly: netobs-gated
 * (zero footprint off the `/debug` surface), sent via `nm.getRoom().send`,
 * fully torn down on unmount. JSON can't carry NaN, so NaN is encoded as
 * null on the wire and re-hydrated server-side.
 */

import React, { useEffect } from 'react';
import { isNetobsEnabled } from './isNetobsEnabled';
import type { AllocSampleWire, AllocSampleSource } from '../../../shared/diagnostics/AllocSampleTypes';

const SAMPLE_INTERVAL_MS = 1000;

interface MemoryShape {
  usedJSHeapSize: number;
  jsHeapSizeLimit: number;
  totalJSHeapSize: number;
}

/** measureUserAgentSpecificMemory() result shape (subset we read). */
interface UASpecificMemoryResult {
  bytes: number;
}

function readSyncMemory(): MemoryShape | null {
  const perf = (globalThis as { performance?: Performance & { memory?: MemoryShape } }).performance;
  if (!perf || !perf.memory) return null;
  return perf.memory;
}

function hasMeasureUAMemory(): boolean {
  return typeof (performance as unknown as { measureUserAgentSpecificMemory?: () => Promise<UASpecificMemoryResult> })
    .measureUserAgentSpecificMemory === 'function';
}

function sendSample(sample: AllocSampleWire): void {
  // JSON can't encode NaN → emit null for unsupported readings; server
  // re-hydrates to NaN. Bytes that are finite pass through untouched.
  const wire = {
    capturedAt: sample.capturedAt,
    heapUsedBytes: Number.isFinite(sample.heapUsedBytes) ? sample.heapUsedBytes : null,
    heapLimitBytes: Number.isFinite(sample.heapLimitBytes) ? sample.heapLimitBytes : null,
    source: sample.source,
  };
  try {
    const room = (globalThis as any).nm?.getRoom?.();
    if (room && typeof room.send === 'function') {
      room.send('netobs:allocSample', wire);
    }
  } catch {
    // diagnostic-only, never gameplay
  }
}

const AllocSampleReporterInner: React.FC = () => {
  useEffect(() => {
    const useUAMemory = hasMeasureUAMemory();
    let cancelled = false;

    const tick = (): void => {
      const capturedAt = Date.now();

      if (useUAMemory) {
        const measure = (performance as unknown as {
          measureUserAgentSpecificMemory: () => Promise<UASpecificMemoryResult>;
        }).measureUserAgentSpecificMemory;
        measure()
          .then((result) => {
            if (cancelled) return;
            const source: AllocSampleSource = 'measureUserAgentSpecificMemory';
            sendSample({
              capturedAt,
              heapUsedBytes: typeof result?.bytes === 'number' ? result.bytes : NaN,
              // measureUserAgentSpecificMemory has no limit field — fall back
              // to performance.memory's limit when available, else NaN.
              heapLimitBytes: readSyncMemory()?.jsHeapSizeLimit ?? NaN,
              source,
            });
          })
          .catch(() => {
            // API can reject (not isolated, throttled). Fall back to sync.
            if (cancelled) return;
            const m = readSyncMemory();
            sendSample({
              capturedAt,
              heapUsedBytes: m ? m.usedJSHeapSize : NaN,
              heapLimitBytes: m ? m.jsHeapSizeLimit : NaN,
              source: m ? 'performance.memory' : 'unsupported',
            });
          });
        return;
      }

      const m = readSyncMemory();
      sendSample({
        capturedAt,
        heapUsedBytes: m ? m.usedJSHeapSize : NaN,
        heapLimitBytes: m ? m.jsHeapSizeLimit : NaN,
        source: m ? 'performance.memory' : 'unsupported',
      });
    };

    const timer = window.setInterval(tick, SAMPLE_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  return null;
};

export const AllocSampleReporter: React.FC = () => {
  if (!isNetobsEnabled()) return null;
  return <AllocSampleReporterInner />;
};
